/**
 * UWM quote → Mana Pricing option rows (one per UWM product × temporary-buydown program),
 * in the same shape the rate-sheet lenders produce, so the results table, the options tray
 * and the borrower emails treat every lender alike.
 *
 * Prices follow UWM's math (uwm-math.ts): the lender-paid buydown factor, CYP and — for a
 * lender-paid comp — the 1% comp adjust UWM's final price; ★ is the shared LP/BP rule. Price is
 * shown 100-based (100 − the final-price points), APR is rate + 0.6.
 */
import {
  buildLenderRateEntry,
  fieldValueList,
  fmtRateAsOf,
  lenderOptionName,
  parseBuydownOption,
  type BuydownChoice,
  type LenderRateRow,
} from '../lender-pricing-fields.js';
import {
  manaApr,
  uwmBuydownConcession,
  uwmBuydownEligibilityKey,
  uwmBuydownForChoice,
  uwmCriteriaFor,
  uwmCriteriaPointIndex,
  uwmEffectiveFinal,
  uwmProductName,
  UWM_BUYDOWN_STEPS,
  type UwmCriteria,
  type UwmCriteriaByFamily,
  type UwmPricePoint,
  type UwmQuoteResult,
} from './uwm-math.js';
import type { UwmLoanFamily } from './bridge.js';

export interface UwmLadderPoint {
  rate: number;
  /** 100-based price (100 − UWM final-price points, after the buydown LLPA and CYP). */
  price: number;
  discountPct: number;
  /** Lender credit in dollars (+) or cost (−). */
  discountDollars: number;
  apr: number;
  pi: number;
  mi: number;
  monthlyPayment: number;
  capped: boolean;
  meetsCriteria: boolean;
  /** Seller/borrower-paid buydown concession at this rate (a closing cost for ★), else null. */
  buydownCost: number | null;
  /** Index of the raw UWM price point (for UWM's pricing breakdown). */
  pointIndex: number;
}

export interface UwmOptionRow {
  lenderKey: 'uwm';
  investor: string;
  productId: string;
  optionId: string;
  product: string;
  termYears: number;
  buydown: BuydownChoice | null;
  /** Headline point: the rate whose price is closest to the desired price. */
  rate: number;
  price: number;
  apr: number;
  discountPct: number;
  discountDollars: number;
  pi: number;
  mi: number;
  monthlyPayment: number;
  buydownCost: number | null;
  starRate: number | null;
  lockPeriod: number;
  ladder: UwmLadderPoint[];
  /** Index of the product in UWM's validQuoteItems. */
  quoteIndex: number;
  /** Lender-paid comp percent netted out of the prices (null = borrower-paid comp). */
  lpCompPct: number | null;
  /** The loan type UWM quoted this product under (when the scenario priced several). */
  loanFamily?: UwmLoanFamily;
  amortization?: 'fixed' | 'arm';
  /** The ★ criteria this row was starred with (its loan type's comp category). */
  criteria?: UwmCriteria | null;
}

export const UWM_INVESTOR_NAME = 'UWM';

/** Option id for a UWM product × term × buydown program (tray + options schedules). */
export function uwmOptionId(productName: string, termYears: number, buydown: BuydownChoice | null): string {
  return `uwm:${productName}@${termYears}y${buydown ? `+${buydown.key.replace(/\s+/g, '')}` : ''}`;
}

/** Parse a uwm: option id back to product name, term and buydown key (null for a sheet id). */
export function parseUwmOptionId(id: string): { productName: string; termYears: number; buydownKey: string | null } | null {
  const m = id.match(/^uwm:(.+)@(\d+)y(?:\+(.+))?$/);
  if (!m) return null;
  return { productName: m[1]!, termYears: Number(m[2]), buydownKey: m[3] ? m[3] : null };
}

const r3 = (v: number) => Math.round(v * 1000) / 1000;

/**
 * The Temporary Buydown programs a scenario prices (null = plain fixed) — the rate sheets'
 * rule (multi-select, de-duplicated, "None" when nothing is picked), so UWM rows line up.
 */
export function buydownChoicesOf(inputs: Record<string, unknown>): Array<BuydownChoice | null> {
  const labels = fieldValueList(inputs.buydown);
  const out: Array<BuydownChoice | null> = [];
  const seen = new Set<string>();
  for (const raw of labels.length ? labels : ['None']) {
    const choice = parseBuydownOption(raw);
    if (!choice && !/^none$/i.test(raw)) continue;
    const key = choice?.key ?? 'none';
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(choice);
  }
  return out.length ? out : [null];
}

export function buildUwmOptionRows(args: {
  result: UwmQuoteResult | null | undefined;
  buydowns: Array<BuydownChoice | null>;
  loanAmount: number;
  cypBps: number;
  criteria: UwmCriteria | null;
  /** ★ criteria per loan type family when UWM priced several (each product uses its own). */
  criteriaByFamily?: UwmCriteriaByFamily | null;
  /** Lender-paid comp percent netted out of every price (null = borrower-paid comp). */
  lpCompPct: number | null;
  desiredPrice: number;
  lockDays: number;
}): UwmOptionRow[] {
  const rows: UwmOptionRow[] = [];
  const products = args.result?.validQuoteItems ?? [];
  products.forEach((q, quoteIndex) => {
    // HELOC products can come back without a term (0 = shown as "—").
    const termYears = q.actualTermYears ?? 0;
    const points = q.quotePricePoints ?? [];
    if (points.length === 0) return;
    const name = uwmProductName(q);
    const criteria = uwmCriteriaFor(q, args.criteria, args.criteriaByFamily);
    for (const choice of args.buydowns) {
      // A temporary-buydown row only where UWM itself offers that buydown on the product (its answer
      // to the quote re-sent with the buydown alias — priceUwm's manaBuydownEligible); no answer = no row.
      if (choice && !(args.result?.manaBuydownEligible?.[uwmBuydownEligibilityKey(choice)] ?? []).includes(name)) continue;
      const sel = uwmBuydownForChoice(choice);
      const steps = sel && !sel.isLlpa ? UWM_BUYDOWN_STEPS[sel.base] : undefined;
      const starIdx = uwmCriteriaPointIndex(points, { buydown: sel, loanAmount: args.loanAmount, termYears, cypBps: args.cypBps, criteria, lpCompPct: args.lpCompPct });
      const ladder: UwmLadderPoint[] = [];
      points.forEach((p: UwmPricePoint, pointIndex) => {
        const rate = p.interestRate?.value;
        if (typeof rate !== 'number') return;
        const eff = uwmEffectiveFinal(p, sel, args.loanAmount, args.cypBps, args.lpCompPct);
        const pct = eff?.percent ?? 0;
        const amount = eff?.amount ?? 0;
        const pi = p.principalAndInterest?.value ?? 0;
        const mi = p.mortgageInsurance?.amount ?? 0;
        ladder.push({
          rate,
          price: r3(100 - pct),
          discountPct: r3(-pct),
          discountDollars: Math.round(-amount),
          apr: manaApr(rate),
          pi,
          mi,
          monthlyPayment: pi + mi,
          capped: false,
          meetsCriteria: pointIndex === starIdx,
          buydownCost: steps && args.loanAmount > 0 ? Math.round(uwmBuydownConcession(rate, termYears, args.loanAmount, steps)) : null,
          pointIndex,
        });
      });
      if (ladder.length === 0) continue;
      ladder.sort((a, b) => a.rate - b.rate);
      let head = ladder[0]!;
      for (const p of ladder) {
        const d = Math.abs(p.price - args.desiredPrice);
        const bd = Math.abs(head.price - args.desiredPrice);
        if (d < bd - 1e-9 || (Math.abs(d - bd) < 1e-9 && p.rate < head.rate)) head = p;
      }
      const star = ladder.find((p) => p.meetsCriteria) ?? null;
      rows.push({
        lenderKey: 'uwm',
        investor: UWM_INVESTOR_NAME,
        productId: `uwm:${name}`,
        optionId: uwmOptionId(name, termYears, choice),
        product: name,
        termYears,
        buydown: choice,
        rate: head.rate,
        price: head.price,
        apr: head.apr,
        discountPct: head.discountPct,
        discountDollars: head.discountDollars,
        pi: head.pi,
        mi: head.mi,
        monthlyPayment: head.monthlyPayment,
        buydownCost: head.buydownCost,
        starRate: star ? star.rate : null,
        lockPeriod: args.lockDays,
        ladder,
        quoteIndex,
        lpCompPct: args.lpCompPct,
        ...(q.manaLoanFamily ? { loanFamily: q.manaLoanFamily } : {}),
        ...(q.manaAmortization ? { amortization: q.manaAmortization } : {}),
        criteria,
      });
    }
  });
  return rows;
}

/** The point a borrower email uses for a UWM option: the ★ rate, else the headline (same rule as the rate sheets). */
export function uwmEmailPointFor(row: UwmOptionRow): UwmLadderPoint {
  const star = row.starRate != null ? row.ladder.find((p) => p.rate === row.starRate) : undefined;
  return star ?? row.ladder.find((p) => p.rate === row.rate) ?? row.ladder[0]!;
}

/**
 * Borrower-facing option for a UWM price point — the tray ("+ Add") and the options-email
 * schedules both build it here. Same columns as a rate-sheet option; "rate as of" = the UWM
 * quote time.
 */
export function uwmRateEntryFor(row: UwmOptionRow, point: UwmLadderPoint, quotedAt: string | Date | null): LenderRateRow {
  const escrow =
    row.buydown?.paidBy === 'borrower' && point.buydownCost != null
      ? `${row.buydown.label} buydown escrow ${point.buydownCost.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })} (not in the price)`
      : null;
  return buildLenderRateEntry({
    productId: row.optionId,
    investor: row.investor,
    product: lenderOptionName(row.product, row.termYears, row.buydown?.label),
    rate: point.rate,
    price: point.price,
    apr: point.apr,
    pi: point.pi,
    discountDollars: point.discountDollars,
    lockPeriod: row.lockPeriod,
    lastUpdated: null,
    sheetVersion: 'UWM live quote',
    // Same note as a rate-sheet option priced with a lender-paid comp.
    comp: row.lpCompPct != null ? `LP comp ${row.lpCompPct.toFixed(3)}%` : null,
    extraNote: escrow,
    asOf: fmtRateAsOf(quotedAt),
  });
}
